import React, {
  useState, useCallback, useMemo, useEffect,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  ArrowUp, ArrowDown, Lock, Plus,
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput.jsx';
import CheckboxInput from '../../components/ui/CheckboxInput';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import {
  useFeedbackFormsApi,
  useFeedbackFormDetailApi,
  useCompetencyNames,
  competencyAsync,
  fetchCompetencyDescription,
} from './config';
import { positionAsync, fetchPositionCompetencies } from '../positions/config';

// Task 33 (Sekme 2) — 360° form listesi (DataTable, ASLA şekil değiştirmez —
// UI-fix demeti #67, bkz. aşağı) + form drawer'ı (ad + ağırlıklar + sorular,
// TEK GİRİŞ NOKTASI). Kilitli formda (FeedbackFormResponse.Locked — aktif bir
// değerlendirme dönemi tarafından kullanılıyor, bkz.
// FeedbackFormDataAccess.MarkLockedAsync) tüm mutasyon eylemleri gizlenir,
// yalnız salt-okunur rozet + görüntüleme kalır.
//
// Faz E (Task 4) — form drawer'ına "Değerlendirici Ağırlıkları" bölümü
// eklendi (task-2-report.md §1: 5 opsiyonel override alanı, ya HEPSİ null ya
// HEPSİ dolu+toplam-100). GenericFormDrawer'ın şema-tabanlı alanları bunu
// TAŞIYAMIYOR — RatingScalesTab.js'in (Skorlama Plan 2, Task 10) AYNI
// gerekçesiyle aynı çözüm: `schema=[]`, ad+ağırlıklar `children` içinde kendi
// taslak state'imizle yönetiliyor, kilitliyken `disabled` geçiliyor (schema
// alanları `disabled` okumuyor).
//
// UI-fix demeti #67 (2026-08-09) — ESKİ tasarımda bir form kaydına
// tıklamak SEKMENİN KENDİSİNİ soru-ekleme moduna dönüştürüyordu (liste →
// "Geri" butonlu detay görünümü, sayfanın ŞEKLİ değişiyordu). Kullanıcı bunu
// "kabul edilemez" buldu. Yeni tasarım: liste HER ZAMAN liste kalır; bir form
// kaydına tıklamak (VEYA "..." menüsünden Düzenle) SAĞDAN bir drawer açar —
// bu TEK drawer artık ad + ağırlıklar + sorular (nested "Sorular" bölümü:
// liste + ekle/düzenle/sil/sırala + yetkinlik etiketi + pozisyon-önerisi,
// hepsi AYNEN Faz E Task 5'ten taşındı) hepsini yönetir. Soru ekleme/düzenleme
// KENDİ İÇİNDE ayrı, bu drawer'ın ÜSTÜNE yığılan (stacked) ikinci bir
// GenericFormDrawer (`questionDrawer`) — react-modern-drawer PORTAL KULLANMAZ
// (normal DOM child, yalnız `position:fixed` ile pinlenir — node_modules
// kaynağında `createPortal`/`ReactDOM` referansı YOK, doğrulandı), iki
// `direction="right"` drawer aynı anda açıkken SONRA mount edilen (soru
// drawer'ı, JSX'te formDrawer'dan SONRA) DOM sırası gereği ÜSTTE görünür —
// "drawer üstüne drawer" deseni, ek bir z-index/offset kodu YAZILMADI.
//
// Salt-okunur genişlemesi (bu görevin DOĞAL sonucu): eskiden bu drawer'a
// giden TEK yol `canManage` (PM.Write) ile kapılıydı (Yeni Form butonu +
// "..." menüsü ikisi de `canManage` şartlıydı, satır tıklaması ise AYRI bir
// salt-okunur "detay" görünümüne gidiyordu). Artık satır tıklaması da AYNI
// drawer'ı açtığı için (canManage şartı OLMADAN — PM.Read kullanıcılar formu
// GÖRÜNTÜLEYEBİLMELİ, eski detay görünümünün de yaptığı gibi), Ad/Ağırlık
// alanlarına `formLocked`e EK olarak `!canManage` de salt-okunur nedeni
// olarak eklendi (`formReadOnly`) — aksi halde salt-okunuz bir kullanıcı
// kilitli-olmayan bir formun adını/ağırlıklarını UI'da "düzenleyebilir"
// görünürdü (BE zaten 403 ile reddederdi ama UX yanıltıcı olurdu).

const LockedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  background: rgba(229, 57, 53, 0.08);
  color: #E53935;
  font-size: 11px;
  font-weight: 700;
`;

const LockedHint = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 16px;
`;

const QuestionsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;

  th {
    text-align: left;
    padding: 8px 10px;
    color: #6c757d;
    font-weight: 600;
    border-bottom: 1px solid #e8ecf0;
    white-space: nowrap;
  }
  td {
    padding: 8px 10px;
    color: ${({ theme }) => theme.colors.text.primary};
    border-bottom: 1px solid #f2f4f7;
    vertical-align: middle;
  }
`;

const OrderCell = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const OrderBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  color: #667;
  &:hover:not(:disabled) { border-color: #0291EF; color: #0291EF; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const EmptyText = styled.p`
  color: #A0A8B4;
  font-size: 13px;
  text-align: center;
  padding: 24px;
`;

// UI-fix demeti #67 — "Sorular" bölümü, artık form drawer'ının İÇİNDE nested
// (eski ayrı "detay sayfası"nın yerini alıyor). WeightsSection'ın AYNI üst
// ayraç (border-top) diliyle görsel olarak bir SONRAKİ bölüm gibi ayrışıyor
// (SuggestSection'ın — soru drawer'ının kendi öneri bölümü — AYNI deseni).
const QuestionsSection = styled.div`
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #E8ECF0;
`;

const QuestionsSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 10px;
`;

const QuestionsSectionTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const NameFieldWrapper = styled.div`
  margin-bottom: 18px;
`;

const WeightsSection = styled.div`
  margin-top: 4px;
  margin-bottom: 10px;
`;

const WeightsSectionTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const WeightsHint = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin: 8px 0;
`;

const WeightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

// PerformanceSettingsPage.js'in TotalWeight'iyle AYNI dengeli/dengesiz ton
// (#1B7A3E / #B02A37) — tenant-genel ağırlık toplamı sayacının form-özel
// varyantı.
const WeightsTotal = styled.p`
  font-size: 13px;
  font-weight: 700;
  margin-top: 8px;
  color: ${({ $balanced }) => ($balanced ? '#1B7A3E' : '#B02A37')};
`;

// RatingScalesTab.js'in SaveErrorText'iyle BİREBİR aynı.
const SaveErrorText = styled.p`
  font-size: 12px;
  color: #B02A37;
  margin: 8px 0;
  font-weight: 600;
`;

// Faz E (Task 5, spec §4) — soru drawer'ının "Pozisyon Yetkinliklerinden Öner"
// bölümü. WeightsSection/WeightsSectionTitle'ın (Task 4) AYNI görsel dili.
const SuggestSection = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #E8ECF0;
`;

const SuggestSectionTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const SuggestHint = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin: 8px 0;
`;

const SuggestList = styled.div`
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  overflow: hidden;
  margin-top: 8px;
`;

const SuggestItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  border: none;
  border-bottom: 1px solid #F2F4F7;
  background: #fff;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  &:last-child { border-bottom: none; }
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;

const SuggestItemLevel = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  color: #6c757d;
`;

// Faz E (Task 4) — task-2-report.md §1: FeedbackFormRequest/Response'un 5
// override alanı, aynı isimlerle. Hepsi null = form tenant varsayılanını
// kullanıyor (BE'de ayrı bir `hasOverride` bool YOK — YAGNI, task-2-report.md
// §1 — bu yüzden burada da varlığı 5 alanın hepsinin null olup olmadığından
// türetilir).
const WEIGHT_OVERRIDE_FIELDS = [
  'weightSelfOverride', 'weightManagerOverride', 'weightPeerOverride',
  'weightSubordinateOverride', 'weightSeniorOverride',
];
const EMPTY_WEIGHT_DRAFT = WEIGHT_OVERRIDE_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {});

export default function FeedbackFormsTab() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const canManage = canWrite('PM');

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [formDrawer, setFormDrawer] = useState({ open: false, row: null });
  const [questionDrawer, setQuestionDrawer] = useState({ open: false, question: null });
  const [deleteFormConfirm, setDeleteFormConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [deleteQuestionConfirm, setDeleteQuestionConfirm] = useState({ isOpen: false, id: null });
  const [busy, setBusy] = useState(false);

  // Faz E (Task 4) — drawer'ın kendi taslak state'i (RatingScalesTab.js'in
  // nameDraft/levels deseninin aynısı): GenericFormDrawer'ın formData'sından
  // BAĞIMSIZ, çünkü `disabled` (kilitli form) ve toggle mantığı schema
  // alanlarıyla ifade edilemiyor (bkz. dosya başı yorumu).
  const [nameDraft, setNameDraft] = useState('');
  const [useTenantDefaultWeights, setUseTenantDefaultWeights] = useState(true);
  const [weightDraft, setWeightDraft] = useState(EMPTY_WEIGHT_DRAFT);
  const [formSaveError, setFormSaveError] = useState('');

  // Faz E (Task 5, spec §4) — soru drawer'ının "Pozisyon Yetkinliklerinden
  // Öner" bölümü: seçilen pozisyonun PositionCompetency listesi (task-3-report.md
  // §1: GET v1/positions/{id}/competencies, form editörünün İKİ tüketicisinden
  // biri — diğeri PositionCompetenciesDrawer.js).
  const [suggestPositionId, setSuggestPositionId] = useState(null);
  const [suggestCompetencies, setSuggestCompetencies] = useState([]);
  const [suggestFetching, setSuggestFetching] = useState(false);

  const formLocked = !!formDrawer.row?.locked;
  // UI-fix demeti #67 — satır tıklaması artık bu drawer'ı `canManage`
  // ŞARTSIZ açıyor (bkz. dosya başı yorumu) — Ad/Ağırlık alanları bu yüzden
  // `formLocked`e EK olarak `!canManage`i de salt-okunur nedeni sayar.
  const formReadOnly = formLocked || !canManage;

  const {
    fetching, data, pagination, columns: baseColumns,
    createForm, renameForm, deleteForm,
  } = useFeedbackFormsApi(queryParams);

  // UI-fix demeti #67 — eskiden ayrı bir "detay sayfası"nın (`selectedId`)
  // hook'uydu; artık DOĞRUDAN form drawer'ının açık olduğu satırın id'sine
  // bağlı (`formDrawer.row?.id`) — drawer kapanınca `formDrawer.row` null
  // olur, hook `formId` düştüğünde form'u zaten temizliyor (config/index.js).
  const {
    form: detail, fetching: detailFetching,
    addQuestion, updateQuestion, removeQuestion, reorderQuestions,
  } = useFeedbackFormDetailApi(formDrawer.row?.id || null);
  // Sorular bölümü mutasyonları YALNIZ form KİLİTLİ DEĞİLKEN + canManage
  // iken — `detail.locked` (taze GET) eski koddaki AYNI kaynak, `formLocked`
  // (satır snapshot'ı) DEĞİL (aynı gerçeği taşırlar ama eski davranış
  // birebir korunuyor).
  const canManageQuestions = canManage && !detail?.locked;

  const competencyIds = useMemo(
    () => (detail?.questions || []).map((q) => q.competencyId).filter(Boolean),
    [detail],
  );
  const competencyNames = useCompetencyNames(competencyIds);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const openNewForm = useCallback(() => setFormDrawer({ open: true, row: null }), []);
  // Faz E (Task 4) — artık kilitli formlarda da açılır (salt-okunur/"Görüntüle"
  // başlığıyla, bkz. dosya başı yorumu) — `condition` aşağıdaki
  // formActionOptions'tan kaldırıldı. UI-fix demeti #67 — artık TEK giriş
  // noktası: hem DataTable satır tıklaması hem "..." menüsünün Düzenle
  // aksiyonu AYNI bu fonksiyonu çağırır.
  const openFormDrawer = useCallback((row) => setFormDrawer({ open: true, row }), []);

  // Faz E (Task 4) — drawer açılışında/satır değişince taslağı hydrate eder
  // (RatingScalesTab.js'in nameDraft/levels useEffect'iyle AYNI desen).
  useEffect(() => {
    if (!formDrawer.open) return;
    setNameDraft(formDrawer.row?.name || '');
    const hasOverride = !!formDrawer.row
      && WEIGHT_OVERRIDE_FIELDS.some((f) => formDrawer.row[f] !== null && formDrawer.row[f] !== undefined);
    setUseTenantDefaultWeights(!hasOverride);
    setWeightDraft(hasOverride
      ? WEIGHT_OVERRIDE_FIELDS.reduce((acc, f) => ({ ...acc, [f]: formDrawer.row[f] ?? '' }), {})
      : EMPTY_WEIGHT_DRAFT);
    setFormSaveError('');
  }, [formDrawer.open, formDrawer.row]);

  // Toggle AÇIK ("tenant varsayılanını kullan") → 5 alan gizlenir VE temizlenir
  // (brief: "açık=5 alan gizli/temiz").
  const handleToggleTenantDefaultWeights = useCallback((val) => {
    setUseTenantDefaultWeights(val);
    if (val) setWeightDraft(EMPTY_WEIGHT_DRAFT);
  }, []);

  const updateWeightDraft = useCallback((field, value) => {
    setWeightDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  // İstemci-ipucu (asıl kapı BE — `performance.feedbackForm.weightOverrideInvalid`,
  // task-2-report.md §1): toplam-100 canlı sayacı, PerformanceSettingsPage.js'in
  // tenant-genel ağırlık toplamı sayacıyla AYNI desen. Kaydetmeyi BLOKLAMAZ.
  const weightDraftSum = useMemo(
    () => WEIGHT_OVERRIDE_FIELDS.reduce((sum, f) => sum + (Number(weightDraft[f]) || 0), 0),
    [weightDraft],
  );

  // UI-fix demeti #67 — form adı başlığa interpole edilir (drawer artık
  // içeriği "Standart 360" gibi ayrı bir DetailTitle başlığında GÖSTERMİYOR,
  // Ad alanı zaten bir input — kullanıcı HANGİ formu düzenlediğini başlıktan
  // anlar). `viewFormTitle`/`editFormTitle` YENİ anahtarlar — eski `rename`/
  // `view` artık kullanılmıyor (kasıtlı, bkz. t3-report.md).
  const formDrawerTitle = useMemo(() => {
    if (!formDrawer.row) return t('performance.settings.forms.newForm');
    return formReadOnly
      ? t('performance.settings.forms.viewFormTitle', { name: formDrawer.row.name })
      : t('performance.settings.forms.editFormTitle', { name: formDrawer.row.name });
  }, [formDrawer.row, formReadOnly, t]);

  // GenericFormDrawer'ın Footer'ındaki Save butonu salt-okunurluğu KENDİSİ
  // disable ETMEZ (schema=[] olduğu için `missing` kontrolü de boş) —
  // RatingScalesTab.handleSave'in AYNI guard'ı: salt-okunur (kilitli VEYA
  // canManage yok) durumda hiçbir ağ isteği çıkmadan sessizce `false` döner.
  const handleFormSave = useCallback(async () => {
    if (formReadOnly) return false;

    const trimmedName = nameDraft.trim();
    if (!trimmedName) {
      setFormSaveError(t('performance.settings.forms.nameRequiredHint'));
      return false;
    }
    setFormSaveError('');

    // Kaydet gövdesine override alanları: toggle açıkken (tenant varsayılanı)
    // 5'i de null; kapalıyken taslaktaki ham string değerler sayıya çevrilir
    // (boş string → null, BE'nin "ya HEPSİ null ya HEPSİ dolu" doğrulamasına
    // düşer — istemci burada bloklamıyor, bkz. weightDraftSum ipucu).
    const weightsPayload = useTenantDefaultWeights
      ? WEIGHT_OVERRIDE_FIELDS.reduce((acc, f) => ({ ...acc, [f]: null }), {})
      : WEIGHT_OVERRIDE_FIELDS.reduce((acc, f) => {
        const raw = weightDraft[f];
        acc[f] = (raw === '' || raw === null || raw === undefined) ? null : Number(raw);
        return acc;
      }, {});

    const payload = { name: trimmedName, ...weightsPayload };
    return formDrawer.row
      ? await renameForm(formDrawer.row.id, payload)
      : await createForm(payload);
  }, [formReadOnly, nameDraft, useTenantDefaultWeights, weightDraft, formDrawer.row, renameForm, createForm, t]);

  const handleDeleteForm = useCallback((row) => {
    setDeleteFormConfirm({ isOpen: true, id: row.id, name: row.name });
  }, []);

  const confirmDeleteForm = useCallback(async () => {
    setBusy(true);
    await deleteForm(deleteFormConfirm.id);
    setBusy(false);
    setDeleteFormConfirm({ isOpen: false, id: null, name: '' });
  }, [deleteForm, deleteFormConfirm.id]);

  // Faz E (Task 4) — Düzenle artık kilitli formda da görünür (drawer
  // salt-okunur açılır, bkz. dosya başı yorumu); Sil kilitliyken hâlâ gizli
  // (BE zaten 409 `locked` ile reddeder — FeedbackFormDataAccess.DeleteAsync).
  // UI-fix demeti #67 — etiket "Adını Değiştir"den `common.edit`e (Düzenle)
  // değişti: drawer artık yalnız ad değil, ağırlıklar + sorular da yönetiyor
  // (CompetenciesTab.js'in "Düzenle" aksiyonuyla AYNI genel etiket deseni).
  const formActionOptions = useMemo(() => {
    if (!canManage) return [];
    return [
      { type: 'edit', label: t('common.edit'), onClick: openFormDrawer },
      { type: 'delete', label: t('common.delete'), onClick: handleDeleteForm, condition: (row) => !row.locked },
    ];
  }, [canManage, t, openFormDrawer, handleDeleteForm]);

  const columns = useMemo(() => {
    if (formActionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            return <OptionsMenu actions={formActionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, formActionOptions, data]);

  const questionTypeOptions = useMemo(() => [
    { id: 'likert', name: t('performance.settings.forms.questionType.likert') },
    { id: 'open_text', name: t('performance.settings.forms.questionType.open_text') },
  ], [t]);

  const questionSchema = useMemo(() => [
    { name: 'text', label: t('performance.settings.forms.questionTextLabel'), type: 'textarea', grid: 12, required: true, rows: 3 },
    { name: 'questionType', label: t('performance.settings.forms.questionTypeLabel'), type: 'select', grid: 6, required: true, options: questionTypeOptions, defaultValue: 'likert' },
    { name: 'competencyId', label: t('performance.settings.forms.competencyLabel'), type: 'asyncSelect', grid: 6, onSearch: competencyAsync.onSearch, onGetById: competencyAsync.onGetById },
  ], [t, questionTypeOptions]);

  const openAddQuestion = useCallback(() => setQuestionDrawer({ open: true, question: null }), []);

  // UI-fix demeti #67 — soru drawer'ı artık form drawer'ının ÜSTÜNE yığılan
  // (stacked) ikinci bir GenericFormDrawer (bkz. dosya başı yorumu). Form
  // drawer'ı kapanırken (satır tıklaması olmadan X/dışına tıklama/Kaydet
  // sonrası) üstündeki soru drawer'ı da açık kalmamalı — savunmacı temizlik.
  useEffect(() => {
    if (!formDrawer.open) {
      setQuestionDrawer({ open: false, question: null });
    }
  }, [formDrawer.open]);

  // Faz E (Task 5) — "Pozisyon Yetkinliklerinden Öner" drawer AÇIKKEN de
  // çalışır (bkz. aşağıdaki handlePickSuggestion): bir öneri tıklandığında
  // `questionDrawer.question` yeni bir taslak objeyle DEĞİŞTİRİLİR (add
  // modundaysa `id` alanı YOK). Bu yüzden add/edit ayrımı artık
  // `questionDrawer.question` truthy'liğine değil, `.id` varlığına bakar —
  // aksi halde taslak-doldurulmuş bir "yeni soru" yanlışlıkla updateQuestion'a
  // (id=undefined) düşerdi.
  const editingQuestionId = questionDrawer.question?.id || null;

  const handleQuestionSave = useCallback(async (values) => {
    const payload = {
      text: values.text,
      questionType: values.questionType || 'likert',
      competencyId: values.competencyId || null,
    };
    return editingQuestionId
      ? await updateQuestion(editingQuestionId, payload)
      : await addQuestion(payload);
  }, [editingQuestionId, updateQuestion, addQuestion]);

  // Faz E (Task 5, spec §4) — drawer kapanınca öneri seçicisini sıfırlar
  // (position + fetched competencies), her yeni "Soru Ekle/Düzenle" açılışı
  // temiz başlar.
  useEffect(() => {
    if (!questionDrawer.open) {
      setSuggestPositionId(null);
      setSuggestCompetencies([]);
    }
  }, [questionDrawer.open]);

  useEffect(() => {
    if (!suggestPositionId) {
      setSuggestCompetencies([]);
      return undefined;
    }
    let cancelled = false;
    setSuggestFetching(true);
    fetchPositionCompetencies(suggestPositionId)
      .then((rows) => { if (!cancelled) setSuggestCompetencies(rows); })
      .catch(() => { if (!cancelled) setSuggestCompetencies([]); })
      .finally(() => { if (!cancelled) setSuggestFetching(false); });
    return () => { cancelled = true; };
  }, [suggestPositionId]);

  // Bir öneriye tıklanınca: yetkinliğin açıklamasını (competencyAsync YALNIZ
  // {id,name} taşır) ayrıca getirip soru metnini yetkinlik adı + açıklamadan
  // kurar (spec §4: "kullanıcı düzenler" — taslak, nihai metin değil).
  // `initialValues` (questionDrawer.question) referansı değiştiği için
  // GenericFormDrawer'ın open/initialValues efekti formData'yı bu taslakla
  // sıfırlar (drawer kapanıp açılmadan, `open` zaten true).
  const handlePickSuggestion = useCallback(async (comp) => {
    const description = await fetchCompetencyDescription(comp.competencyId);
    const suggestedText = description
      ? t('positionCompetencies.suggestedQuestionTemplate', { name: comp.competencyName, description })
      : comp.competencyName;
    setQuestionDrawer((prev) => ({
      ...prev,
      question: {
        ...(prev.question || {}),
        text: suggestedText,
        questionType: 'likert',
        competencyId: comp.competencyId,
      },
    }));
  }, [t]);

  const confirmDeleteQuestion = useCallback(async () => {
    setBusy(true);
    await removeQuestion(deleteQuestionConfirm.id);
    setBusy(false);
    setDeleteQuestionConfirm({ isOpen: false, id: null });
  }, [removeQuestion, deleteQuestionConfirm.id]);

  const handleMove = useCallback(async (idx, dir) => {
    if (!detail) return;
    const qs = detail.questions || [];
    const otherIdx = idx + dir;
    if (otherIdx < 0 || otherIdx >= qs.length) return;
    setBusy(true);
    await reorderQuestions([
      { id: qs[idx].id, sortOrder: qs[otherIdx].sortOrder },
      { id: qs[otherIdx].id, sortOrder: qs[idx].sortOrder },
    ]);
    setBusy(false);
  }, [detail, reorderQuestions]);

  const questionActionOptions = useMemo(() => [
    { type: 'edit', label: t('common.edit'), onClick: (row) => setQuestionDrawer({ open: true, question: row }) },
    { type: 'divider' },
    { type: 'delete', label: t('common.delete'), onClick: (row) => setDeleteQuestionConfirm({ isOpen: true, id: row.id }) },
  ], [t]);

  const questions = detail?.questions || [];

  return (
    <div>
      <GenericFormDrawer
        open={formDrawer.open}
        onClose={() => setFormDrawer({ open: false, row: null })}
        title={formDrawerTitle}
        schema={[]}
        onSave={handleFormSave}
      >
        {formLocked && (
          <LockedBadge><Lock size={12} /> {t('performance.settings.forms.lockedBadge')}</LockedBadge>
        )}

        <NameFieldWrapper>
          <TextInput
            label={t('performance.settings.forms.nameLabel')}
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            disabled={formReadOnly}
            required
          />
        </NameFieldWrapper>

        <WeightsSection>
          <WeightsSectionTitle>{t('formWeights.sectionTitle')}</WeightsSectionTitle>
          <CheckboxInput
            label={t('formWeights.useTenantDefaultToggle')}
            checked={useTenantDefaultWeights}
            onChange={formReadOnly ? undefined : handleToggleTenantDefaultWeights}
          />
          {!useTenantDefaultWeights && (
            <>
              <WeightsHint>{t('performance.settings.weights.hint')}</WeightsHint>
              <WeightsGrid>
                <TextInput
                  label={t('performance.settings.weights.self')}
                  type="number"
                  value={weightDraft.weightSelfOverride}
                  onChange={(e) => updateWeightDraft('weightSelfOverride', e.target.value)}
                  disabled={formReadOnly}
                />
                <TextInput
                  label={t('performance.settings.weights.manager')}
                  type="number"
                  value={weightDraft.weightManagerOverride}
                  onChange={(e) => updateWeightDraft('weightManagerOverride', e.target.value)}
                  disabled={formReadOnly}
                />
                <TextInput
                  label={t('performance.settings.weights.peer')}
                  type="number"
                  value={weightDraft.weightPeerOverride}
                  onChange={(e) => updateWeightDraft('weightPeerOverride', e.target.value)}
                  disabled={formReadOnly}
                />
                <TextInput
                  label={t('performance.settings.weights.subordinate')}
                  type="number"
                  value={weightDraft.weightSubordinateOverride}
                  onChange={(e) => updateWeightDraft('weightSubordinateOverride', e.target.value)}
                  disabled={formReadOnly}
                />
                <TextInput
                  label={t('performance.settings.weights.senior')}
                  type="number"
                  value={weightDraft.weightSeniorOverride}
                  onChange={(e) => updateWeightDraft('weightSeniorOverride', e.target.value)}
                  disabled={formReadOnly}
                />
              </WeightsGrid>
              <WeightsTotal $balanced={weightDraftSum === 100}>
                {t('performance.settings.weights.total')}: {weightDraftSum}% / 100%
              </WeightsTotal>
            </>
          )}
        </WeightsSection>

        {formSaveError && <SaveErrorText>{formSaveError}</SaveErrorText>}

        {formLocked && <LockedHint>{t('performance.settings.forms.lockedHint')}</LockedHint>}

        {/* UI-fix demeti #67 — "Sorular", eski ayrı "detay sayfası"nın yerini
            alan NESTED bölüm (liste + ekle/düzenle/sil/sırala + yetkinlik
            etiketi — hepsi AYNEN Faz E Task 5'ten taşındı). Yalnız MEVCUT bir
            form için (`formDrawer.row?.id` — yeni formda henüz id yok, soru
            eklenemez, eski davranışla AYNI). */}
        {formDrawer.row?.id && (
          <QuestionsSection>
            <QuestionsSectionHeader>
              <QuestionsSectionTitle>{t('performance.settings.forms.questionsSectionTitle')}</QuestionsSectionTitle>
              {/* #72 — yerel AddQuestionButton kaldırıldı, paylaşılan Button'a
                  bağlandı (T2'nin "PrimaryCta" tarifi artık Button.js'in
                  varsayılan "primary" varyantı — bkz. t6-report.md). */}
              {canManageQuestions && (
                <Button type="button" onClick={openAddQuestion}>
                  <Plus size={14} />
                  {t('performance.settings.forms.addQuestion')}
                </Button>
              )}
            </QuestionsSectionHeader>

            {detailFetching ? (
              <EmptyText>{t('common.loading')}</EmptyText>
            ) : questions.length === 0 ? (
              <EmptyText>{t('performance.settings.forms.noQuestions')}</EmptyText>
            ) : (
              <QuestionsTable>
                <thead>
                  <tr>
                    <th>{t('performance.settings.forms.orderColumn')}</th>
                    <th>{t('performance.settings.forms.textColumn')}</th>
                    <th>{t('performance.settings.forms.typeColumn')}</th>
                    <th>{t('performance.settings.forms.competencyColumn')}</th>
                    {canManageQuestions && <th>{t('common.actions')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {questions.map((q, idx) => (
                    <tr key={q.id}>
                      <td>
                        {canManageQuestions ? (
                          <OrderCell>
                            <OrderBtn
                              title={t('performance.settings.forms.moveUp')}
                              disabled={idx === 0 || busy}
                              onClick={() => handleMove(idx, -1)}
                            >
                              <ArrowUp size={13} />
                            </OrderBtn>
                            <OrderBtn
                              title={t('performance.settings.forms.moveDown')}
                              disabled={idx === questions.length - 1 || busy}
                              onClick={() => handleMove(idx, 1)}
                            >
                              <ArrowDown size={13} />
                            </OrderBtn>
                          </OrderCell>
                        ) : (idx + 1)}
                      </td>
                      <td>{q.text}</td>
                      <td>{t(`performance.settings.forms.questionType.${q.questionType}`)}</td>
                      <td>{q.competencyId ? (competencyNames[q.competencyId] || '...') : '-'}</td>
                      {canManageQuestions && (
                        <td>
                          <OptionsMenu actions={questionActionOptions} row={q} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </QuestionsTable>
            )}
          </QuestionsSection>
        )}
      </GenericFormDrawer>

      <GenericFormDrawer
        open={questionDrawer.open}
        onClose={() => setQuestionDrawer({ open: false, question: null })}
        title={editingQuestionId ? t('performance.settings.forms.editQuestion') : t('performance.settings.forms.addQuestion')}
        schema={questionSchema}
        initialValues={questionDrawer.question}
        onSave={handleQuestionSave}
      >
        {/* Faz E (Task 5, spec §4) — "Pozisyon Yetkinliklerinden Öner": pozisyon
            seç → PositionCompetency listesi (task-3-report.md §1, GET
            v1/positions/{id}/competencies) → tıkla → CompetencyId etiketli
            likert soru taslağı (bkz. handlePickSuggestion). */}
        <SuggestSection>
          <SuggestSectionTitle>{t('positionCompetencies.suggestSectionTitle')}</SuggestSectionTitle>
          <AsyncGenericSelect
            label={t('positionCompetencies.suggestPositionLabel')}
            value={suggestPositionId}
            onChange={setSuggestPositionId}
            onSearch={positionAsync.onSearch}
            onGetById={positionAsync.onGetById}
            renderOption={positionAsync.renderOption}
            searchPlaceholder={t('position.pickerSearchPlaceholder')}
          />
          {suggestPositionId && (
            suggestFetching ? (
              <SuggestHint>{t('common.loading')}</SuggestHint>
            ) : suggestCompetencies.length === 0 ? (
              <SuggestHint>{t('positionCompetencies.suggestEmptyHint')}</SuggestHint>
            ) : (
              <>
                <SuggestHint>{t('positionCompetencies.suggestHint')}</SuggestHint>
                <SuggestList>
                  {suggestCompetencies.map((c) => (
                    <SuggestItem
                      key={c.competencyId}
                      type="button"
                      onClick={() => handlePickSuggestion(c)}
                    >
                      <span>{c.competencyName}</span>
                      <SuggestItemLevel>
                        {t('positionCompetencies.targetLevelLabel')}: {c.targetLevel}
                      </SuggestItemLevel>
                    </SuggestItem>
                  ))}
                </SuggestList>
              </>
            )
          )}
        </SuggestSection>
      </GenericFormDrawer>

      <ConfirmDialog
        isOpen={deleteFormConfirm.isOpen}
        title={t('performance.common.deleteTitle')}
        message={t('performance.common.deleteMessage', { name: deleteFormConfirm.name })}
        onConfirm={confirmDeleteForm}
        onCancel={() => setDeleteFormConfirm({ isOpen: false, id: null, name: '' })}
        loading={busy}
      />

      <ConfirmDialog
        isOpen={deleteQuestionConfirm.isOpen}
        title={t('performance.settings.forms.deleteQuestionConfirmTitle')}
        message={t('performance.settings.forms.deleteQuestionConfirmMessage')}
        onConfirm={confirmDeleteQuestion}
        onCancel={() => setDeleteQuestionConfirm({ isOpen: false, id: null })}
        loading={busy}
      />

      {/* UI-fix demeti #67 — liste ARTIK KOŞULSUZ render edilir (sekmenin
          şekli hiç değişmez); satır tıklaması form drawer'ını açar. */}
      <DataTable
        title={t('performance.settings.forms.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canManage}
        onNewButtonClick={openNewForm}
        onRowClick={openFormDrawer}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
      />
    </div>
  );
}
